import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAllVideos, deleteVideo } from "../../services/videoService";

export default function VideoListPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all|active|inactive
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const data = await getAllVideos();
      setVideos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return videos.filter((v) => {
      const matchesQ =
        !q ||
        (v.title || "").toLowerCase().includes(q.toLowerCase()) ||
        (v.uplodedBy || "").toLowerCase().includes(q.toLowerCase());
      const matchesActive =
        activeFilter === "all" ||
        (activeFilter === "active" && !!v.isActive) ||
        (activeFilter === "inactive" && !v.isActive);
      return matchesQ && matchesActive;
    });
  }, [videos, q, activeFilter]);

  const handleDelete = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this video?");
    if (!ok) return;

    try {
      setDeletingId(id);
      await deleteVideo(id);
      // optimistic update
      setVideos((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      alert(e.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Videos</h4>
        <Link to="/admin/videos/create" className="btn btn-primary">+ Add Video</Link>
      </div>

      <div className="card p-3 shadow-sm mb-3">
        <div className="row g-2">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Search by title or uploader"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="col-md-3">
            <button className="btn btn-outline-secondary w-100" onClick={load} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <p>Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="alert alert-warning">No videos found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>Title</th>
                <th>Uploader</th>
                <th>Uploaded Date</th>
                <th>Active</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id}>
                  <td>{v.id}</td>
                  <td className="text-truncate" style={{ maxWidth: 340 }}>{v.title || <em>(no title)</em>}</td>
                  <td>{v.uplodedBy || "-"}</td>
                  <td>{v.uplodedDate ? String(v.uplodedDate).slice(0, 10) : "-"}</td>
                  <td>
                    {v.isActive ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td className="d-flex gap-2">
                   <Link to={`/admin/videos/${v.id}/edit`} className="btn btn-sm btn-outline-primary">
  Edit
</Link>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(v.id)}
                      disabled={deletingId === v.id}
                    >
                      {deletingId === v.id ? "Deleting..." : "Delete"}
                    </button>
                    {v.videoUrl && (
                      <Link to={v.videoUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
                        View
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
